import { useCallback, useState } from 'react';

export interface UseExpandedGroupsResult {
  isExpanded: (notificationId: string) => boolean;
  toggle: (notificationId: string) => void;
}

/**
 * Per-item collapsed/expanded UI state for grouped notifications (F33: "Design
 * both the collapsed and expanded forms, and the +N more affordance").
 * `notifications` rows already collapse a burst sharing a `groupingKey` into
 * ONE row server-side (`groupCount`, `packages/schemas/src/comms.ts`) with a
 * pre-rendered summary `title`/`body` — this is the CLIENT-side reveal of
 * that row's full `body` (e.g. "Alice, Bob, Carol" behind a "12 people
 * liked…" title) rather than a second grouping mechanism. Plain component
 * state (not persisted) — collapsed-by-default every time the panel opens,
 * same precedent as `unreadOnly`/`page` in `useNotifications.ts`.
 */
export function useExpandedGroups(): UseExpandedGroupsResult {
  const [expanded, setExpanded] = useState<ReadonlySet<string>>(new Set());

  const isExpanded = useCallback((notificationId: string) => expanded.has(notificationId), [expanded]);

  const toggle = useCallback((notificationId: string) => {
    setExpanded((prev) => {
      const next = new Set(prev);
      if (next.has(notificationId)) next.delete(notificationId);
      else next.add(notificationId);
      return next;
    });
  }, []);

  return { isExpanded, toggle };
}
